import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FrontdeskService } from '../../../services/frontdesk/frontdesk.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// perform a check-in for one booking. data = { requisitionId, familyCode, name }.
// the dialog loads the booking's guests and provisioned rooms so the operator
// can note who sleeps where and who was physically verified. room allotment is
// optional: bookings without online room rows check in without it.
@Component({
  selector: 'app-checkin-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, LoaderComponent],
  templateUrl: './checkin-dialog.component.html',
  styleUrls: ['./checkin-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class CheckinDialogComponent implements OnInit {
  form: FormGroup;
  isLoading: boolean = false;
  guests: any[] = [];
  rooms: any[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CheckinDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { requisitionId: number; familyCode: string; name: string },
    private frontdeskService: FrontdeskService,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      inRegisterSigned: [false],
      keyHandedOverUser: ['', Validators.required],
      inRegSignedUser: ['', Validators.required],
      checkinComments: [''],
      // the operator's verification confirmation gates the button (never stored)
      finalCheck: [false],
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.frontdeskService.getCheckinContext(this.data.requisitionId).subscribe({
      next: (res: any) => {
        const context = res?.data || {};
        this.rooms = context.rooms || [];
        // per-guest working state for the allotment table
        this.guests = (context.guests || []).map((g: any) => ({
          ...g,
          roomCatalogueInstanceId: g.roomCatalogueInstanceId || '',
          isPhysicalVerified: g.isPhysicalVerified === true,
        }));
        this.isLoading = false;
      },
      error: () => {
        // the desk can still check in without the allotment step
        this.guests = [];
        this.rooms = [];
        this.isLoading = false;
      },
    });
  }

  // every guest row has its own requisition-guest id, so the key is unique
  // for devotees, visitors, and manual walk-in guests alike.
  guestKey(g: any): string {
    return String(g?.srddId ?? '');
  }

  // verification flags live on the devotee / visitor master; manual walk-in
  // guests have no master record, so their checkbox is hidden.
  canVerify(g: any): boolean {
    return !!(g?.devoteeId || g?.visitorId);
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error('Key handed over by and In-register signed by are required.');
      return;
    }
    if (!this.form.value.finalCheck) {
      this.toastService.error('Confirm the identity verification first.');
      return;
    }
    this.isLoading = true;
    const devoteeAllotmentDetails = this.guests
      .filter((g) => g.devoteeId)
      .map((g) => ({
        devoteeId: g.devoteeId,
        roomCatalogueInstanceId: g.roomCatalogueInstanceId || null,
        isPhysicalVerified: !!g.isPhysicalVerified,
      }));
    const visitorAllotmentDetails = this.guests
      .filter((g) => !g.devoteeId && g.visitorId)
      .map((g) => ({
        visitorId: g.visitorId,
        roomCatalogueInstanceId: g.roomCatalogueInstanceId || null,
        isPhysicalVerified: !!g.isPhysicalVerified,
      }));
    const manualAllotmentDetails = this.guests
      .filter((g) => !g.devoteeId && !g.visitorId && g.srddId)
      .map((g) => ({
        srddId: g.srddId,
        roomCatalogueInstanceId: g.roomCatalogueInstanceId || null,
      }));

    const payload = {
      requisitionId: this.data.requisitionId,
      inRegisterSigned: !!this.form.value.inRegisterSigned,
      keyHandedOverUser: this.form.value.keyHandedOverUser,
      inRegSignedUser: this.form.value.inRegSignedUser,
      checkinComments: this.form.value.checkinComments || '',
      devoteeAllotmentDetails,
      visitorAllotmentDetails,
      manualAllotmentDetails,
    };
    this.frontdeskService.checkin(payload).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Checked in.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Check-in failed.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
